import React, { useMemo, useEffect } from 'react';
import {
  useTable,
  useSortBy,
  useRowSelect,
  useExpanded,
  Column,
  CellProps,
  HeaderGroup,
  Row,
  ColumnInstance,
  TableState,
  SortingRule,
  usePagination,
} from 'react-table';
import cx from 'classnames';

import Box from '../Box';
import Checkbox from '../Checkbox';
import IconButton from '../IconButton';
import Text from '../Text';
import Table from '../Table';
import ChevronDownS1 from '../Icons/ChevronDownS1';
import ChevronUpS1 from '../Icons/ChevronUpS1';
import LoadingSpinnerNoBits from '../LoadingSpinnerNoBits';
import Pagination from '../Pagination';
import { Props as FilterProps } from '../Filter';
import { Props as SearchInputProps } from '../SearchInput';

import useUniqueId from '../utils/useUniqueId';

import styles from './TableGroup.module.scss';

interface WithPaginationProps {
  /** If `true`, will render pagination component. */
  hasPagination: true;
  /** Configuration for pagination. */
  pagination?: {
    /** Specific the number of rows per page. */
    rowsPerPage?: number;
    /** Callback when page is changed. This will provide an object containing `newPageNum` and `pageCount` as a argument. */
    onPageChange?: ({
      newPageNum,
      pageCount,
    }: {
      newPageNum: number;
      pageCount: number;
    }) => void;
  };
}
interface WithOutPaginationProps {
  pagination?: never;
  hasPagination?: false;
}
interface BaseProps {
  /** Section for table bulk actions (like exporting data as csv). Should typically be a primary and an optional secondary button. */
  bulkActions?: React.ReactNode;
  /** Override table styles by adding a className. */
  className?: Parameters<typeof cx>[0];
  /** Headers rendered within the table. */
  columns: Array<Column>;
  /** A customize empty state that will appear when no rows are present. */
  emptyState?: React.ReactNode;
  /** A customize error state that will appear when an error occurs. */
  errorState?: React.ReactNode;
  /** Content to be rendered in expanded row.  */
  expandedRow?: (row: Row<object>) => React.ReactNode;
  /** Section for filtering the table using `Filter.Group`. Will be displayed above the table.  */
  filters?: React.ReactElement<FilterProps>;
  /** If `true`, hides the table label visually. */
  hasLabel?: boolean;
  /** Accessible name for the Table, which will be visually hidden by default. */
  label: string;
  /** Set the initial state for the table which will be passed to react-table. This can be used to have a default expanded row or sort. */
  initialState?: TableState<object>;
  /** If `true`, the generic error state will appear. */
  isError?: boolean;
  /** If `true`, a loading spinner will appear and the table will not be interactive. */
  isLoading?: boolean;
  /** Callback when table is sorted. */
  onSortedChange?: (tableSort: SortingRule<object>[]) => void;
  /** Callback when row selection changes. This will provide `selectedRows` as a argument.*/
  onSelectedRowsChange?: (selectedRows: Row<object>[]) => void;
  /** Data rendered within the table. */
  rows: Array<Row>;
  /** Call to action section that is typically an `IconButton` which would be appended at the end of row. */
  rowActions?: (
    getAllTableProps: React.PropsWithChildren<CellProps<object>>,
  ) => JSX.Element | undefined;
  /** Section for a text input, which should be a `SearchInput`. */
  search?: React.ReactElement<SearchInputProps>;
  /** Specific `TableGroup` variant type. */
  variant?: 'default' | 'selectable' | 'expandable' | 'selectable-expandable';
}

// Conditional intersection type that varies depending on whether it's a TableGroup with or without pagination.
export type TableGroupProps = BaseProps &
  (WithOutPaginationProps | WithPaginationProps);

function hideColumns(
  currentType: TableGroupProps['variant'],
  hasRowActions: TableGroupProps['rowActions'],
) {
  const typeHiddenColumnENUM = {
    default: ['selection', 'expander'],
    selectable: ['expander'],
    expandable: ['selection'],
    'selectable-expandable': [],
  };

  return [
    ...typeHiddenColumnENUM[currentType || 'default'],
    hasRowActions === undefined ? 'rowActions' : '',
  ];
}

export const TableGroup: React.FC<TableGroupProps> = ({
  bulkActions,
  className,
  columns,
  emptyState,
  errorState,
  expandedRow,
  filters,
  hasLabel = true,
  hasPagination = false,
  label,
  initialState,
  isError = false,
  isLoading = false,
  onSelectedRowsChange,
  onSortedChange,
  pagination,
  rows: propRows,
  rowActions,
  search,
  variant = 'default',
}) => {
  const { rowsPerPage, onPageChange } = pagination ?? {};
  const isEmpty = propRows.length <= 0;

  const memoColumns = useMemo(() => columns, [columns]);
  const memoRows = useMemo(() => propRows, [propRows]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
    selectedFlatRows,
    state: { sortBy, pageIndex },
    page,
    pageCount,
    gotoPage,
    setPageSize,
  } = useTable(
    {
      columns: memoColumns,
      data: memoRows,
      initialState: {
        pageIndex: 0,
        hiddenColumns: hideColumns(variant, rowActions),
        ...initialState,
      },
    },
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    hooks => {
      hooks.visibleColumns.push((currentColumns: Array<Column>) => {
        return [
          {
            id: 'expander',
            Header: ' ',
            disableSortBy: true,
            Cell: ({ row, toggleAllRowsExpanded }) => {
              return (
                <IconButton
                  {...row.getToggleRowExpandedProps()}
                  onClick={() => {
                    if (row.isExpanded === undefined) {
                      toggleAllRowsExpanded(false);
                    }
                    row.toggleRowExpanded();
                  }}
                  accessibilityLabel={`Toggle expand row ${row.index + 1} of ${
                    rows.length
                  }, currently ${row.isExpanded ? 'expanded' : 'collapsed'}.`}
                  icon={row.isExpanded ? <ChevronUpS1 /> : <ChevronDownS1 />}
                  className={styles.expandBtn}
                />
              );
            },
          },
          {
            id: 'selection',
            disableSortBy: true,
            Header: row => {
              const {
                checked,
                title,
                ...restProps
              } = row.getToggleAllRowsSelectedProps();

              return (
                <Checkbox
                  {...restProps}
                  id={useUniqueId()}
                  value={checked || false}
                  hideLabel={true}
                  className={styles.checkbox}
                  onChange={() => row.toggleAllRowsSelected()}
                >
                  Select all rows on this table.
                </Checkbox>
              );
            },
            Cell: ({ row: { getToggleRowSelectedProps, index, id } }) => {
              const {
                checked,
                title,
                onChange,
                ...restProps
              } = getToggleRowSelectedProps();

              // TODO: Explore announcement of the number of selected rows after onChange: https://plaidinc.atlassian.net/browse/THREAD-910
              return (
                <Checkbox
                  id={useUniqueId(id)}
                  value={checked || false}
                  onChange={event => onChange && onChange(event)}
                  hideLabel={true}
                  className={styles.checkbox}
                  {...restProps}
                >
                  {`${title} row ${index + 1} of ${rows.length}`}
                </Checkbox>
              );
            },
          },
          ...currentColumns,
          {
            id: 'rowActions',
            Header: () => (
              <span className={cx(styles.rowActions)}>Row Actions</span>
            ),
            disableSortBy: true,
            Cell: rowActions === undefined ? '' : rowActions,
          },
        ];
      });
    },
  );

  // Invoke onSelectedRowsChange whenever there is a change in the selectedFlatRows.
  useEffect(() => {
    if (onSelectedRowsChange) {
      onSelectedRowsChange(selectedFlatRows);
      return;
    }
  }, [selectedFlatRows, onSelectedRowsChange]);

  // Invoke onSortedChange whenever there is a change in the sortBy parameter, passing along the updated values.
  useEffect(() => {
    if (onSortedChange) {
      onSortedChange(sortBy);
      return;
    }
  }, [sortBy, onSortedChange]);

  // Will set the table rows when rowsPerPage is supplied.
  useEffect(() => {
    if (rowsPerPage) {
      setPageSize(rowsPerPage);
      return;
    }
  }, [setPageSize, rowsPerPage]);

  if (isLoading) {
    return (
      <div className={styles.loadingGroup}>
        <LoadingSpinnerNoBits className={styles.loader} />
        <Text as='h2' size={16} weight='semiBold' className={styles.title}>
          Loading
        </Text>
      </div>
    );
  }
  // TODO: Replace with emptyState component when released
  // https://plaidinc.atlassian.net/browse/THREAD-876
  if (isError) {
    return (
      <>
        {errorState ? (
          errorState
        ) : (
          <div className={styles.unknownState}>
            <div className={styles.group}>
              <Text
                as='h2'
                size={16}
                weight='semiBold'
                className={styles.title}
              >
                Uh oh, something has gone wrong
              </Text>
              <Text>Please try reloading or checking back later.</Text>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {hasLabel && (
        <Box mb={2}>
          <Text as='h2' size={24} weight='semiBold' mt={0} mb={0}>
            {label}
          </Text>
        </Box>
      )}
      {(search || bulkActions) && (
        <Box
          mb={2}
          display='grid'
          gridTemplateColumns={search && bulkActions ? 'repeat(2, 1fr)' : 'fr'}
        >
          {search && <Box>{search}</Box>}
          {bulkActions && (
            <Box justifySelf='flex-end' display='flex' alignSelf='center'>
              {bulkActions}
            </Box>
          )}
        </Box>
      )}
      {filters && <Box mb={2}>{filters}</Box>}
      <Table
        {...getTableProps()}
        className={cx(styles.tableGroup, isEmpty && styles.isEmpty, className)}
        label={label}
      >
        <Table.Head>
          {headerGroups.map((headerGroup, i) => {
            return (
              <Table.Row {...headerGroup.getHeaderGroupProps()} key={i}>
                {headerGroup.headers.map(
                  (
                    column: HeaderGroup<object> & {
                      align?: 'start' | 'center' | 'end';
                      setWidth?: string;
                    },
                  ) => {
                    return (
                      <Table.HeadCell
                        {...column.getHeaderProps(
                          column.getSortByToggleProps(),
                        )}
                        disableSortBy={column.disableSortBy || false}
                        sort={
                          column.isSorted
                            ? column.isSortedDesc
                              ? 'descending'
                              : 'ascending'
                            : 'none'
                        }
                        key={column.id}
                        align={column?.align}
                        width={column?.setWidth}
                        className={cx(
                          column.id === 'selection' && styles.checkboxColumn,
                          column.id === 'expander' && styles.expanderColumn,
                          column.id === 'rowActions' && styles.rowActionsColumn,
                        )}
                      >
                        {column.render('Header')}
                      </Table.HeadCell>
                    );
                  },
                )}
              </Table.Row>
            );
          })}
        </Table.Head>
        <Table.Body {...getTableBodyProps()}>
          {page.map(row => {
            prepareRow(row);
            return (
              <React.Fragment key={row.id}>
                <Table.Row {...row.getRowProps()} id={row.id}>
                  {row.cells.map(
                    (
                      {
                        getCellProps,
                        column,
                        render,
                      }: {
                        getCellProps: any;
                        column: ColumnInstance<object> & {
                          font?: 'sans' | 'monospace';
                          align?: 'start' | 'center' | 'end';
                          renderDataCell?: (
                            cell: React.ReactNode,
                          ) => JSX.Element;
                        };
                        render: (
                          type: string,
                          userProps?: object | undefined,
                        ) => React.ReactNode;
                      },
                      i,
                    ) => {
                      return (
                        <Table.Cell
                          {...getCellProps}
                          key={column.id + i}
                          font={
                            column.font === 'monospace' ? 'monospace' : 'sans'
                          }
                          align={column?.align}
                          cellWrapperClassName={cx(
                            column.id === 'expander' && styles.expanderWrapper,
                            column.id === 'rowActions' &&
                              styles.rowActionsWrapper,
                          )}
                          className={cx(
                            column.id === 'rowActions' && styles.rowActionsCell,
                          )}
                        >
                          {column.renderDataCell
                            ? column.renderDataCell(render('Cell'))
                            : render('Cell')}
                        </Table.Cell>
                      );
                    },
                  )}
                </Table.Row>
                <Table.RowExpanded
                  isExpanded={row.isExpanded}
                  colSpan={visibleColumns.length}
                  cellWrapperClassName={cx(
                    variant === 'selectable-expandable' &&
                      styles.isSelectableAndExpandable,
                    variant === 'expandable' && styles.isExpandable,
                  )}
                >
                  {expandedRow && expandedRow(row)}
                </Table.RowExpanded>
              </React.Fragment>
            );
          })}
        </Table.Body>
      </Table>
      {isEmpty && (
        <>
          {emptyState ? (
            emptyState
          ) : (
            <div className={cx(styles.unknownState)}>
              <div className={styles.group}>
                <Text
                  as='h2'
                  size={16}
                  weight='semiBold'
                  className={styles.title}
                >
                  Nothing could be found
                </Text>
                <Text>Please try refining your search or filters.</Text>
              </div>
            </div>
          )}
        </>
      )}

      {hasPagination && (
        <Box mt={3}>
          <Pagination
            currentPage={pageIndex + 1}
            onPageChange={(newPageNum: number) => {
              gotoPage(newPageNum - 1);

              if (onPageChange) {
                onPageChange({ newPageNum, pageCount });
                return;
              }
            }}
            totalPages={pageCount}
          />
        </Box>
      )}
    </>
  );
};

TableGroup.displayName = 'TableGroup';
export default TableGroup;
export { styles };
